import { Link } from 'react-router';
import { api } from '../lib/api';
import { useSession } from '../lib/session';
import { useResource } from '../lib/useResource';
import type { Connectivity, DeviceSummary, ProfileName } from '../lib/types';
import { CONNECTIVITY_LABEL, PROFILE_LABEL, relativeTime } from '../lib/labels';
import {
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  PageHeader,
  Table,
  errorMessage,
} from '../components/ui';

const CONN_TONE: Record<Connectivity, 'emerald' | 'amber' | 'slate' | 'red'> = {
  online: 'emerald',
  stale: 'amber',
  never: 'slate',
  revoked: 'red',
};

export function ProfilePair({
  requested,
  effective,
}: {
  requested: ProfileName;
  effective: ProfileName;
}) {
  const same = requested === effective;
  return (
    <span className="inline-flex flex-wrap items-center gap-1">
      <Badge>{PROFILE_LABEL[requested]}</Badge>
      <span aria-hidden className="text-slate-400">
        →
      </span>
      <Badge tone={same ? 'indigo' : 'amber'}>{PROFILE_LABEL[effective]}</Badge>
      <span className="sr-only">
        {same ? 'aplicado como solicitado' : 'perfil efetivo diferente do solicitado'}
      </span>
    </span>
  );
}

export function ConnectivityBadge({ c }: { c: Connectivity }) {
  return <Badge tone={CONN_TONE[c]}>{CONNECTIVITY_LABEL[c]}</Badge>;
}

export function DevicesPage() {
  const { tenant, can } = useSession();
  const devices = useResource(
    () => api.get<DeviceSummary[]>('/v1/devices', { tenant: true }),
    [tenant?.id],
  );

  return (
    <>
      <PageHeader
        title="Dispositivos"
        description="Perfil solicitado → perfil efetivo: o efetivo é o que o aparelho realmente permite, detectado pelo agente."
        actions={
          <div className="flex gap-2">
            <Button
              variant="secondary"
              onClick={() => void devices.reload()}
              busy={devices.loading}
            >
              Atualizar
            </Button>
            {can('devices:manage') && (
              <Link
                to="/ativacao"
                className="inline-flex items-center rounded-md bg-indigo-600 px-3.5 py-2 text-sm font-medium text-white hover:bg-indigo-700"
              >
                Ativar dispositivo
              </Link>
            )}
          </div>
        }
      />
      <Card>
        {devices.error != null && <Alert tone="error">{errorMessage(devices.error)}</Alert>}
        <Table
          head={[
            'Aparelho',
            'Android',
            'Perfil (solicitado → efetivo)',
            'Situação',
            'Último contato',
            'Grupo',
          ]}
          empty={
            !devices.loading &&
            devices.data?.length === 0 && <EmptyState>Nenhum dispositivo pareado ainda.</EmptyState>
          }
        >
          {devices.data?.map((d) => (
            <tr key={d.id} className={d.status === 'REVOKED' ? 'opacity-60' : undefined}>
              <td className="px-4 py-2">
                <Link
                  to={`/dispositivos/${d.id}`}
                  className="font-medium text-indigo-700 hover:underline"
                >
                  {d.name}
                </Link>
                <div className="text-xs text-slate-500">agente {d.agentVersion}</div>
              </td>
              <td className="px-4 py-2 whitespace-nowrap text-slate-600">
                {d.androidRelease} <span className="text-xs text-slate-400">(API {d.sdkInt})</span>
              </td>
              <td className="px-4 py-2">
                <ProfilePair requested={d.requestedProfile} effective={d.effectiveProfile} />
              </td>
              <td className="px-4 py-2">
                <ConnectivityBadge c={d.connectivity} />
              </td>
              <td className="px-4 py-2 whitespace-nowrap text-slate-600">
                {relativeTime(d.lastSeenAt)}
              </td>
              <td className="px-4 py-2 text-slate-600">{d.group?.name ?? '—'}</td>
            </tr>
          ))}
        </Table>
      </Card>
    </>
  );
}
